import type { Page, ReportQuery } from '@chukua/common';
import { Table, type TableColumnsType } from 'antd';
import { ChartColumn, SearchX } from 'lucide-react';
import { Link } from 'react-router';
import { EmptyState } from '../../../shared/components/EmptyState';
import { ListSearch } from '../../../shared/components/ListSearch';
import type { ListQueryControls } from '../../../shared/hooks/use-list-query';
import type { ReportFigure } from '../reports.api';
import { drillLink } from '../policies/drill-link.policy';
import { formatMetric } from '../policies/metric-format.policy';

interface ReportFiguresTableProps {
  page: Page<ReportFigure>;
  list: ListQueryControls<ReportQuery>;
  isRefreshing: boolean;
}

function buildColumns(list: ListQueryControls<ReportQuery>): TableColumnsType<ReportFigure> {
  return [
    { key: 'label', title: 'Figure', dataIndex: 'label', sorter: true, sortOrder: list.sortOrderFor('label') },
    {
      key: 'value',
      title: 'Value',
      align: 'right',
      sorter: true,
      sortOrder: list.sortOrderFor('value'),
      // The number is the link: every figure opens the records behind it, under the figure's own filters.
      render: (_, figure) => (
        <Link
          className="font-medium text-link tabular-nums hover:underline"
          to={drillLink(figure.drillThrough)}
          aria-label={`${figure.label}: ${formatMetric(figure.metric)}, show records`}
        >
          {formatMetric(figure.metric)}
        </Link>
      ),
    },
  ];
}

/** The figures, server-paged; the old rows stay up while the next page loads. */
export function ReportFiguresTable({ page, list, isRefreshing }: ReportFiguresTableProps) {
  const emptyState = list.isFiltered ? (
    <EmptyState icon={SearchX} title="Nothing matches" description="No figures match this search or these filters. Widen the period or clear a filter." />
  ) : (
    <EmptyState icon={ChartColumn} title="No figures yet" description="This report has nothing to show for now." />
  );
  return (
    <div className="flex flex-col gap-4">
      <ListSearch value={list.query.q} placeholder="Search figures" onSearch={(q) => list.setQuery({ q }, { replace: true })} />
      <Table
        rowKey={(figure) => JSON.stringify(figure.group)}
        dataSource={page.items}
        columns={buildColumns(list)}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: emptyState }}
        {...list.tableProps<ReportFigure>(page.total)}
      />
    </div>
  );
}
